import React, { useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

interface MetaGeneratorProps {
  currentSport?: 'NBA' | 'NFL' | 'MLB' | 'NHL' | 'Soccer';
  gameCount?: number;
  competitions?: string[];
  title?: string;
  description?: string;
  keywords?: string[];
}

const MetaGenerator: React.FC<MetaGeneratorProps> = ({
  currentSport,
  gameCount = 0,
  competitions = [],
  title,
  description,
  keywords
}) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  useEffect(() => {
    const maxValue = searchParams.get('max');
    const minValue = searchParams.get('min');
    const hasFilters = maxValue || minValue;

    const generateTitle = (): string => {
      const sportNames = {
        NBA: 'NBA Basketball',
        NFL: 'NFL Football',
        MLB: 'MLB Baseball',
        NHL: 'NHL Hockey',
        Soccer: 'Soccer/Football'
      };

      const sportName = currentSport ? sportNames[currentSport] : 'Sports';
      let titleStart = `${sportName} Games Without Spoilers`;

      if (competitions.length === 1) {
        titleStart = `${competitions[0]} ${currentSport === 'Soccer' ? 'Games' : sportName} Without Spoilers`;
      } else if (competitions.length > 1) {
        titleStart = `${currentSport ? sportNames[currentSport] : 'Sports'} - ${competitions.join(', ')} Games Without Spoilers`;
      }

      let title = titleStart;

      if (hasFilters) {
        if (maxValue && minValue) {
          title += ` - Filter by Score Range (${minValue}-${maxValue})`;
        } else if (maxValue) {
          title += ` - Close Games (Max ${maxValue} Point Difference)`;
        } else if (minValue) {
          title += ` - High-Scoring Games (Min ${minValue} Points)`;
        }
      }

      if (gameCount > 0) {
        title += ` - ${gameCount} Games Available`;
      }

      title += ' | skore.info';
      return title;
    };

    const generateDescription = (): string => {
      const sportNames = {
        NBA: 'NBA basketball',
        NFL: 'NFL football',
        MLB: 'MLB baseball',
        NHL: 'NHL hockey',
        Soccer: 'soccer'
      };

      const sportName = currentSport ? sportNames[currentSport] : 'sports';
      let baseDescription = `Find exciting ${sportName} games to watch without knowing the scores.`;

      if (competitions.length === 1) {
        baseDescription = `Find exciting ${competitions[0]} ${currentSport === 'Soccer' ? 'games' : sportName.toLowerCase()} to watch without knowing the scores.`;
      } else if (competitions.length > 1) {
        baseDescription = `Find exciting ${sportName} games from competitions like ${competitions.slice(0, 3).join(', ')}${competitions.length > 3 ? ' and more' : ''} to watch without knowing the scores.`;
      }

      let description = baseDescription;

      if (hasFilters) {
        if (maxValue && minValue) {
          description += ` Currently showing games with score differences between ${minValue} and ${maxValue} points.`;
        } else if (maxValue) {
          description += ` Currently showing close games with maximum ${maxValue} point differences.`;
        } else if (minValue) {
          description += ` Currently showing high-scoring games with minimum ${minValue} points.`;
        }
      } else {
        description += ' Filter games by excitement level and enjoy the best action spoiler-free.';
      }

      if (gameCount > 0) {
        let gameNoun = 'games';
        if (competitions.length === 1) {
          gameNoun = competitions[0].endsWith('League') || competitions[0].endsWith('Cup') ? 'matches' : 'games';
        } else if (currentSport === 'Soccer') {
          gameNoun = 'matches';
        }
        description += ` Browse ${gameCount} available ${gameNoun}.`;
      }

      return description;
    };

    const generateKeywords = (): string => {
      const baseKeywords = ['spoiler-free', 'sports', 'games', 'highlights', 'excitement filters'];

      if (currentSport) {
        const sportKeywords = {
          NBA: ['NBA', 'basketball', 'NBA games', 'basketball highlights'],
          NFL: ['NFL', 'football', 'NFL games', 'football highlights'],
          MLB: ['MLB', 'baseball', 'MLB games', 'baseball highlights'],
          NHL: ['NHL', 'hockey', 'NHL games', 'hockey highlights'],
          Soccer: ['soccer', 'football', 'soccer games', 'football highlights']
        };
        baseKeywords.push(...sportKeywords[currentSport]);
      }

      if (competitions.length > 0) {
        baseKeywords.push(...competitions.map(c => c.toLowerCase())); // Add competitions as keywords
      }

      if (hasFilters) {
        baseKeywords.push('filtered games', 'close games', 'exciting matches', 'score filter');
      }
      
      // Add specific filter values to keywords if present
      if (maxValue) baseKeywords.push(`max score diff ${maxValue}`);
      if (minValue) baseKeywords.push(`min score ${minValue}`);

      return [...new Set(baseKeywords)].join(', '); // Ensure unique keywords
    };

    const updateMetaTag = (name: string, content: string, property?: string) => {
      const selector = property ? `meta[property=\"${property}\"]` : `meta[name=\"${name}\"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;

      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    const finalTitle = title || generateTitle();
    const finalDescription = description || generateDescription();
    const finalKeywords = keywords ? keywords.join(', ') : generateKeywords();

    document.title = finalTitle;
    updateMetaTag('description', finalDescription);
    updateMetaTag('keywords', finalKeywords);

    updateMetaTag('', finalTitle, 'og:title');
    updateMetaTag('', finalDescription, 'og:description');
    updateMetaTag('', `https://skore.info${location.pathname}`, 'og:url');
    updateMetaTag('', 'website', 'og:type');
    updateMetaTag('', 'https://skore.info/opengraph-image.png', 'og:image');

    updateMetaTag('twitter:title', finalTitle);
    updateMetaTag('twitter:description', finalDescription);
    updateMetaTag('twitter:image', 'https://skore.info/opengraph-image.png');
    if (!document.querySelector('meta[name="twitter:card"]')) {
      updateMetaTag('twitter:card', 'summary_large_image');
    }

    let canonical = document.querySelector('link[rel=\"canonical\"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://skore.info${location.pathname}`);

  }, [currentSport, gameCount, JSON.stringify(competitions), searchParams, location, title, description, keywords]);

  return null;
};

export default MetaGenerator; 
